import { createMediaStyles } from 'lib/styled-helpers'
import React, { FC, useEffect } from 'react'
import styled from 'styled-components'

import { multiplyRems } from '../../application/theme/utils'
import { getProjectName } from '../../lib/get-project-name'
import { Icon } from '../icon'

export type Props = {
  onCloseRequest: () => void
  visible: boolean
  title?: string
  fullWidth?: boolean
  mediumWidth?: boolean
}

export const ModalWindowContent: FC<Props> = React.memo(
  ({ onCloseRequest, visible, title, children, fullWidth, mediumWidth = false, ...restProps }) => {
    useEffect(() => {
      const handler = (evt: KeyboardEvent) => evt.code === 'Escape' && onCloseRequest()

      window.addEventListener('keydown', handler)
      return () => window.removeEventListener('keydown', handler)
    }, [onCloseRequest])

    return (
      <StyledModalWindow visible={visible} {...restProps}>
        <Cover onClick={onCloseRequest} />
        <Container visible={visible} fullWidth={fullWidth} mediumWidth={mediumWidth}>
          {title ? (
            <Head>
              <Title>{title}</Title>
              <IconWrapper onClick={onCloseRequest}>
                <Icon name={'cancel'} />
              </IconWrapper>
            </Head>
          ) : (
            <Head>
              <IconWrapper onClick={onCloseRequest}>
                <Icon name={'cancel'} />
              </IconWrapper>
            </Head>
          )}
          <Content>{children}</Content>
        </Container>
      </StyledModalWindow>
    )
  }
)

const StyledModalWindow = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: ${({ visible }) => (visible ? '0' : '-100vw')};
  display: flex;
  justify-content: center;
  align-items: center;

  width: 100vw;
  height: 100vh;

  z-index: ${({ visible }) => (visible ? '101' : '-1')};
  opacity: ${({ visible }) => (visible ? '1' : '0')};

  transition: opacity 0.3s;
  overflow: hidden;
  will-change: transform;
`

const Cover = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
`

const Container = styled.div<{ visible: boolean; fullWidth?: boolean; mediumWidth?: boolean }>`
  color: ${({ theme }) => theme.palette.grey[900]};
  ${({ theme }) => theme.textStyles.p1};
  width: 100%;
  z-index: 999;
  opacity: ${({ visible }) => (visible ? '1' : '0')};

  transition: all 0.3s;
  will-change: transform;

  ${({ theme, fullWidth, mediumWidth }) =>
    createMediaStyles(() => ({
      tablet: `width: ${fullWidth ? '85vw' : multiplyRems(theme.spacing[1], 187)};
      width: ${mediumWidth && '65rem'};`,
      desktop: `width: ${mediumWidth && '96rem'}`
    }))}
`

const Head = styled.div`
  position: absolute;
  background-color: transparent;
  text-align: center;
  height: 50px;
  width: 100%;
  top: -50px;
`

const Title = styled.span`
  ${({ theme }) => theme.textStyles.h5};
`

const IconWrapper = styled.div`
  position: absolute;
  right: -0.5rem;
  top: ${({ theme }) => theme.spacing[4]};
  line-height: 1;
  color: ${({ theme }) => theme.palette.grey[500]};

  &:hover {
    color: ${({ theme }) => theme.palette.grey[200]};
    cursor: pointer;
  }
`

const Content = styled.div`
  background-color: ${({ theme }) => theme.palette.white};
  overflow-y: auto;
`
